// First we need to import axios.js
import axios from "axios";
import { baseUrl } from "../config";
import { isEmbeddedDashboard, isAccessedFromQBurstApps } from "./helpers";
import { UserDetails } from "./types";
import useLocalStorage from "../hooks/useLocalStorage";

// Next we make an 'instance' of it
const instance = axios.create({
  // .. where we make our configurations
  baseURL: baseUrl,
  withCredentials: true,
});
instance.defaults.xsrfHeaderName = "X-CSRFToken";
instance.defaults.xsrfCookieName = "csrftoken";
// Where you would set stuff like your 'Authorization' header, etc ...

// Also add/ configure interceptors && all the other cool stuff

instance.interceptors.request.use(
  (request) => {
    const [localUserData] = useLocalStorage<UserDetails | null>(
      "userDetails",
      null,
    );

    if (isEmbeddedDashboard()) {
      const [guestToken] = useLocalStorage("guestToken", null);
      request.headers.Authorization = `Bearer ${guestToken}`;
    } else if (!request.headers.Authorization && localUserData) {
      const token = localUserData.access_token;
      if (token) request.headers.Authorization = `Bearer ${token}`;
    }

    return request;
  },
  (error) =>
    Promise.reject(error instanceof Error ? error : new Error(String(error))),
);

// Handle sign out
const handleSignOut = () => {
  console.log("Could not generate refresh token, user signed out");
  localStorage.clear();
  if (!isEmbeddedDashboard()) window.location.href = "/login";
};

const tryRedirectQburstAppsUnauthorized = (error: any): boolean => {
  if (error.response?.status === 401 && error.response?.data?.redirect_url) {
    if (isAccessedFromQBurstApps()) {
      console.log(
        "401 Unauthorized - Redirecting to:",
        error.response.data.redirect_url,
      );
      globalThis.window.location.href = error.response.data.redirect_url;
      return true;
    }
  }
  return false;
};

const getRetryCountAfterLoginError = async (
  error: unknown,
  retryCount: number,
  maxRetries: number,
  waitBeforeRetry: (count: number) => Promise<unknown>,
): Promise<number | null> => {
  console.error("QBurst Apps login error:", error);
  if (tryRedirectQburstAppsUnauthorized(error)) {
    return null;
  }

  const nextRetryCount = retryCount + 1;
  if (nextRetryCount < maxRetries) {
    await waitBeforeRetry(nextRetryCount);
    return nextRetryCount;
  }

  return null;
};

const qburstAppsLogin = async (
  setLocalUserData: (data: UserDetails) => void,
  maxRetries: number = 2,
): Promise<string | null> => {
  let retryCount = 0;
  const waitBeforeRetry = (count: number) =>
    new Promise((resolve) => setTimeout(resolve, 1000 * count));

  while (retryCount < maxRetries) {
    try {
      const response = await axios.post(
        `${baseUrl}/qburst-apps/login`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        },
      );

      if (
        response.status === 200 &&
        response.data?.message === "QBurst Apps authentication failed."
      ) {
        retryCount++;
        if (retryCount >= maxRetries) {
          console.error("QBurst Apps authentication failed after retries");
          return null;
        }
        await waitBeforeRetry(retryCount);
        continue;
      }

      if (response.status === 200 && response.data?.access) {
        const token = response.data.access;
        const updatedUserData: UserDetails = {
          access_token: token,
          refresh_token: response.data.refresh ?? "",
          email: response.data.email ?? "",
          name: response.data.name ?? "",
          profile_pic: response.data.profile_image ?? "",
          id: response.data.id ?? 0,
          roles: response.data.roles ?? [],
          has_primary: response.data.has_primary_dashboard ?? false,
          virtual_team: response.data.virtual_team ?? "",
          has_ceo_roles_access: response.data.has_ceo_roles_access ?? false,
        };
        setLocalUserData(updatedUserData);
        return token;
      }

      return null;
    } catch (error) {
      const nextRetryCount = await getRetryCountAfterLoginError(
        error,
        retryCount,
        maxRetries,
        waitBeforeRetry,
      );
      if (nextRetryCount === null) {
        return null;
      }
      retryCount = nextRetryCount;
    }
  }

  return null;
};

// Handle refresh token
const refreshToken = async (
  localUserData: UserDetails,
  setLocalUserData: (data: UserDetails) => void,
) => {
  // If accessed from QBurst Apps domain, use QBurst Apps login instead
  if (isAccessedFromQBurstApps()) {
    return await qburstAppsLogin(setLocalUserData);
  }

  // Existing refresh token flow for direct access
  const refresh_token = localUserData.refresh_token;

  if (!refresh_token) {
    // If no refresh token and accessed from QBurst Apps, try QBurst Apps login
    if (isAccessedFromQBurstApps()) {
      return await qburstAppsLogin(setLocalUserData);
    }
    handleSignOut();
    return null;
  }

  try {
    const response = await axios.post(`${baseUrl}/refresh/`, {
      refresh_token,
    });

    if (response.status === 200 && response.data) {
      const token = response.data.access;
      localUserData.access_token = token;
      setLocalUserData(localUserData);
      return token;
    }
  } catch (error) {
    console.error("Token refresh failed:", error);
    // If refresh fails and accessed from QBurst Apps, try QBurst Apps login
    if (isAccessedFromQBurstApps()) {
      return await qburstAppsLogin(setLocalUserData);
    }
  }

  handleSignOut();
  return null;
};

// Add a response interceptor
const isUnauthorizedWithoutRetry = (error: {
  response?: { status?: number };
  config?: { _retry?: boolean };
}): boolean => error.response?.status === 401 && !error.config?._retry;

instance.interceptors.response.use(
  async (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If the error status is 401 and there is no originalRequest._retry flag,
    // it means the token has expired and we need to refresh it
    if (isUnauthorizedWithoutRetry(error)) {
      originalRequest._retry = true;

      try {
        const [localUserData, setLocalUserData] =
          useLocalStorage<UserDetails | null>("userDetails", null);

        if (!localUserData) {
          // If no user data and accessed from QBurst Apps, try QBurst Apps login
          if (isAccessedFromQBurstApps()) {
            const token = await qburstAppsLogin(setLocalUserData);
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }
          }
          throw new Error("no local user details");
        }

        const token = await refreshToken(localUserData, setLocalUserData);
        if (token) {
          // Retry the original request with the new token
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return instance(originalRequest);
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        handleSignOut();
      }
    }

    return Promise.reject(
      error instanceof Error ? error : new Error(String(error)),
    );
  },
);

export default instance;
